import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Link, useLocation } from 'react-router-dom';
import { ArcImage as Image } from '@/components/ui/arc-image';
import { useState } from 'react';
import type { LibraryEntry, Game } from '@/types';
import type { Profile } from '@/types';

interface LibrarySidebarProps {
  initialLibraryEntries: Array<{ entry: LibraryEntry; game: Game }>;
  profile: Profile | null;
}

export function LibrarySidebar({ initialLibraryEntries, profile }: LibrarySidebarProps) {
  const { pathname } = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'installed' | 'pinned'>('all');
  const [libraryGames] = useState<Array<{ entry: LibraryEntry; game: Game }>>(initialLibraryEntries);

  const filteredGames = libraryGames
    .filter(item => {
      if (searchQuery && !item.game.title.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      if (filter === 'installed' && item.entry.status !== 'installed') return false;
      if (filter === 'pinned' && !item.entry.pinned) return false;
      return true;
    })
    .sort((a, b) => {
      if (a.entry.status === 'installed' && b.entry.status !== 'installed') return -1;
      if (a.entry.status !== 'installed' && b.entry.status === 'installed') return 1;
      if (a.entry.pinned && !b.entry.pinned) return -1;
      if (!a.entry.pinned && b.entry.pinned) return 1;
      return 0;
    });

  const installed = filteredGames.filter(item => item.entry.status === 'installed');
  const notInstalled = filteredGames.filter(item => item.entry.status !== 'installed');

  return (
    <div className="arc-aside-surface fixed left-0 top-16 z-30 hidden h-[calc(100vh-4rem)] w-64 flex-col overflow-y-auto md:flex">
      <div className="border-b border-white/10 px-6 pb-6 pt-6">
        <h2 className="font-display text-[10px] font-bold uppercase tracking-widest text-nav-active">Library</h2>
        <p className="mt-1 font-sans text-xs text-slate-500">Developer Tools</p>
      </div>
      {/* Search */}
      <div className="border-b border-white/10 p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input
            placeholder="Search Library"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="glass"
            className="h-9 pl-9"
          />
        </div>
      </div>

      {/* Filter */}
      <div className="border-b border-white/10 p-3">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
          className="glass-input-cutout h-9 w-full rounded-md border border-white/10 bg-transparent px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-400/30 focus:border-blue-400/50"
        >
          <option value="all">All Games</option>
          <option value="installed">Installed Only</option>
          <option value="pinned">Favorites</option>
        </select>
      </div>

      {/* Library List */}
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-4">
          {installed.length > 0 && (
            <div>
              <div className="mb-2 px-2 py-1 text-xs font-semibold uppercase text-slate-500">
                Installed
              </div>
              <div className="space-y-1">
                {installed.map(({ entry, game }) => {
                  const active = pathname === `/library/${game.slug}`;
                  return (
                  <Link
                    key={entry.id}
                    to={`/library/${game.slug}`}
                    className={`group flex items-center gap-2 rounded-lg p-2 transition-colors ${
                      active
                        ? 'bg-arc-primary/15 ring-1 ring-arc-primary/25'
                        : 'hover:bg-white/5'
                    }`}
                  >
                    <div className="w-12 h-12 rounded bg-arc-panel-soft border border-arc-border overflow-hidden flex-shrink-0">
                      <Image
                        src={game.thumbnailUrl}
                        alt={game.title}
                        width={48}
                        height={48}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-arc-text-main truncate">
                        {game.title}
                      </div>
                      <div className="text-xs text-slate-500">
                        {entry.status === 'installed' && 'Ready to play'}
                      </div>
                    </div>
                    {entry.pinned && (
                      <Badge variant="gold" className="text-xs">★</Badge>
                    )}
                  </Link>
                );
                })}
              </div>
            </div>
          )}

          {notInstalled.length > 0 && (
            <div>
              <div className="px-2 py-1 text-xs font-semibold text-arc-text-muted uppercase mb-2">
                Not Installed
              </div>
              <div className="space-y-1">
                {notInstalled.map(({ entry, game }) => {
                  const active = pathname === `/library/${game.slug}`;
                  return (
                    <Link
                      key={entry.id}
                      to={`/library/${game.slug}`}
                      className={`group flex items-center gap-2 rounded-lg p-2 transition-colors ${
                        active
                          ? 'bg-blue-500/10 ring-1 ring-blue-400/30'
                          : 'hover:bg-white/5'
                      }`}
                    >
                      <div className="flex h-12 w-12 flex-shrink-0 overflow-hidden rounded border border-arc-border bg-arc-panel-soft opacity-60">
                        <Image
                          src={game.thumbnailUrl}
                          alt={game.title}
                          width={48}
                          height={48}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-arc-text-muted">{game.title}</div>
                        <div className="text-xs text-arc-text-muted">Not installed</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {filteredGames.length === 0 ? (
            <div className="p-4 text-center font-sans text-sm text-slate-500">
              {!profile ? 'Sign in to view your library' : searchQuery ? 'No games found' : 'No games in library'}
            </div>
          ) : null}
        </div>
      </ScrollArea>
    </div>
  );
}

